// Stock.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../config/axiosConfig';
import {
  Container,
  AppBar,
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import InteractiveStockChart from './InteractiveStockChart';
import CompanyProfile from './CompanyProfile';
import EnhancedCompanyRating from './EnhancedCompanyRating';
import CombinedFinancials from './CombinedFinancials';
import CompanyHistoricalDividendsV2 from './CompanyHistoricalDividendsV2';
import EarningsTranscript from './EarningsTranscript';
import PriceTargetBySymbol from './PriceTargetBySymbol';
import AddToWatchlistModal from './AddToWatchlistModal';
import { useTheme } from '@mui/material/styles';
import { StarOutline as StarIcon } from '@mui/icons-material';
import { useAuth } from './AuthContext';
import ReturnComparison from './ReturnComparison';
import RevenueSegmentation from './RevenueSegmentation';
import MarketStatusIndicator from './MarketStatusIndicator';
import StockPriceContainer from './StockPriceContainer';
import CombineInsiderTrading from './CombineInsiderTrading';
import CompanyLogo from './CompanyLogo';
import Earnings from './Earnings';
import StockStatsPanel from './StockStatsPanel';

// Define sections with their metadata
const sectionConfig = [
  {
    id: 'overview',
    label: 'Overview',
    component: CompanyProfile,
    endpoint: symbol => `/api/companyprofile/${symbol}`
  },
  {
    id: 'financials',
    label: 'Financials',
    component: CombinedFinancials,
    endpoint: symbol => ({
      incomeStatement: `/api/companyincomestatement/${symbol}`,
      balanceSheet: `/api/company-balance-sheet-statement/${symbol}`,
      cashStatement: `/api/company-cash-statement/${symbol}`,
      incomeStatementGrowth: `/api/companyincomestatementgrowth/${symbol}`,
      balanceSheetGrowth: `/api/companybalancesheetgrowth/${symbol}`,
      cashStatementGrowth: `/api/companycashstatementgrowth/${symbol}`,
      keyMetricsTTM: `/api/company-key-metrics-ttm/${symbol}`,
      financialRatiosTTM: `/api/company-financial-ratios-ttm/${symbol}`
    })
  },
  {
    id: 'segmentation',
    label: 'Revenue Breakdown',
    component: RevenueSegmentation,
    endpoint: symbol => `/api/revenueanalysis/${symbol}`
  },
  {
    id: 'ratingsAndScores',
    label: 'Ratings & Scores',
    component: EnhancedCompanyRating,
    endpoint: symbol => `/api/company-rating-and-scores/${symbol}`
  },
  {
    id: 'priceTargets',
    label: 'Price Targets',
    component: PriceTargetBySymbol,
    endpoint: symbol => `/api/priceTargets/symbol/${symbol}`
  },
  // {
  //   id: 'dividends',
  //   label: 'Dividends',
  //   component: CompanyHistoricalDividends,
  //   endpoint: symbol => `/api/historicaldividendsdata/${symbol}`,
  // },
  {
    id: 'dividends',
    label: 'Dividends',
    component: CompanyHistoricalDividendsV2,
    endpoint: symbol => `/api/historicaldividendsdatav2/${symbol}`,
  },
  {
    id: 'CombineInsiderTrading',
    label: 'Insider Trading',
    component: CombineInsiderTrading,
    endpoint: symbol => `/api/insider-trading-statistics/${symbol}`
  },
  {
    id: 'earnings',
    label: 'Earnings',
    component: Earnings,
    endpoint: symbol => `/api/earnings/${symbol}`
  },
  {
    id: 'earningsTranscript',
    label: 'Transcript',
    component: EarningsTranscript,
    endpoint: symbol => `/api/earnings-transcript/${symbol}`
  }
];

// Map URL strings (kebab-case) to section IDs
const urlToSectionId = {
  'overview': 'overview',
  'financials': 'financials',
  'revenue-breakdown': 'segmentation',
  'ratings-and-scores': 'ratingsAndScores',
  'price-targets': 'priceTargets',
  'dividends': 'dividends',
  'insider-trading': 'CombineInsiderTrading',
  'earnings': 'earnings',
};

// Reverse mapping: section ID to URL string
const sectionIdToUrl = {
  'overview': 'overview',
  'financials': 'financials',
  'segmentation': 'revenue-breakdown',
  'ratingsAndScores': 'ratings-and-scores',
  'priceTargets': 'price-targets',
  'dividends': 'dividends',
  'CombineInsiderTrading': 'insider-trading',
  'earnings': 'earnings',
};

const Stock = () => {
  const { symbol: rawSymbol, tab: urlTab, subtab: urlSubtab } = useParams();
  const symbol = rawSymbol.toUpperCase();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  // Base state
  const [selectedTab, setSelectedTab] = useState(null); // Will be set after data loads
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddToWatchlistModal, setShowAddToWatchlistModal] = useState(false);

  // New state for section management
  const [sectionData, setSectionData] = useState({});
  const [sectionLoading, setSectionLoading] = useState({});
  const [availableSections, setAvailableSections] = useState([]);
  const [sectionsLoadComplete, setSectionsLoadComplete] = useState(false);

  const [, setPriceFlash] = useState(false);
  const [, setPriceIncreased] = useState(null);
  const previousPrice = React.useRef(null);
  const [holidayInfo, setHolidayInfo] = useState(null);

  const [assetType, setAssetType] = useState('stock');
  const activeEndpointRef = useRef(null);

  useEffect(() => {
    // Reset states at start
    setStockData(null);
    setLoading(true);
    setSectionData({});
    setSectionLoading({});
    setAvailableSections([]);
    setSelectedTab(null);  // ← Keep it null until we know which tab
    setSectionsLoadComplete(false);
    previousPrice.current = null;
    activeEndpointRef.current = null;

    // Define fetchSectionData inside useEffect
    const fetchSectionData = async (sectionId) => {
      const section = sectionConfig.find(s => s.id === sectionId);

      setSectionLoading(prev => ({ ...prev, [sectionId]: true }));

      try {
        // Handle combined financials differently
        if (sectionId === 'financials') {
          const endpoints = section.endpoint(symbol);

          // Fetch both regular statements AND growth data
          const [
            incomeResponse,
            balanceResponse,
            cashResponse,
            incomeGrowthResponse,
            balanceGrowthResponse,
            cashGrowthResponse,
            keyMetricsTTMResponse,
            financialRatiosTTMResponse
          ] = await Promise.all([
            axios.get(endpoints.incomeStatement),
            axios.get(endpoints.balanceSheet),
            axios.get(endpoints.cashStatement),
            axios.get(endpoints.incomeStatementGrowth),
            axios.get(endpoints.balanceSheetGrowth),
            axios.get(endpoints.cashStatementGrowth),
            axios.get(endpoints.keyMetricsTTM).catch(() => ({ data: { data: null } })),
            axios.get(endpoints.financialRatiosTTM).catch(() => ({ data: { data: null } }))
          ]);

          // Check if we have BOTH main data AND growth data for any statement type
          const hasIncomeData =
            incomeResponse.data?.data?.annual?.length > 0 &&
            incomeGrowthResponse.data?.data?.annual?.length > 0;

          const hasBalanceData =
            balanceResponse.data?.data?.annual?.length > 0 &&
            balanceGrowthResponse.data?.data?.annual?.length > 0;

          const hasCashData =
            cashResponse.data?.data?.annual?.length > 0 &&
            cashGrowthResponse.data?.data?.annual?.length > 0;

          // Only show financials tab if we have at least ONE complete dataset (main + growth)
          if (hasIncomeData || hasBalanceData || hasCashData) {
            setSectionData(prev => ({
              ...prev,
              [sectionId]: {
                incomeStatement: incomeResponse.data.data,
                balanceSheet: balanceResponse.data.data,
                cashStatement: cashResponse.data.data,
                incomeStatementGrowth: incomeGrowthResponse.data.data,
                balanceSheetGrowth: balanceGrowthResponse.data.data,
                cashStatementGrowth: cashGrowthResponse.data.data,
                keyMetricsTTM: keyMetricsTTMResponse.data.data,
                financialRatiosTTM: financialRatiosTTMResponse.data.data
              }
            }));

            setAvailableSections(prev => {
              if (!prev.includes(sectionId)) {
                return [...prev, sectionId].sort((a, b) =>
                  sectionConfig.findIndex(s => s.id === a) -
                  sectionConfig.findIndex(s => s.id === b)
                );
              }
              return prev;
            });
          } else {
            // console.log('Financials tab hidden - missing data or growth data');
          }
        } else if (sectionId === 'earnings') {
          const response = await axios.get(section.endpoint(symbol));
          const hasEarningsReport = response.data?.data?.earningsReport?.length > 0;
          const hasOwnerEarnings = response.data?.data?.ownerEarnings?.length > 0;

          if (hasEarningsReport || hasOwnerEarnings) {
            setSectionData(prev => ({
              ...prev,
              [sectionId]: response.data.data
            }));
            setAvailableSections(prev => {
              if (!prev.includes(sectionId)) {
                return [...prev, sectionId].sort((a, b) =>
                  sectionConfig.findIndex(s => s.id === a) -
                  sectionConfig.findIndex(s => s.id === b)
                );
              }
              return prev;
            });
          }
        } else {
          // Original code for other sections
          const response = await axios.get(section.endpoint(symbol));

          if (!response.data.hasOwnProperty('hasData')) {
            const hasData = response.data.data &&
              (Array.isArray(response.data.data) ? response.data.data.length > 0 : true);

            if (hasData) {
              setSectionData(prev => ({
                ...prev,
                [sectionId]: response.data.data
              }));

              // earningsTranscript is handled as a subtab inside Earnings, skip adding as top-level tab
              if (sectionId !== 'earningsTranscript') {
                setAvailableSections(prev => {
                  if (!prev.includes(sectionId)) {
                    return [...prev, sectionId].sort((a, b) =>
                      sectionConfig.findIndex(s => s.id === a) -
                      sectionConfig.findIndex(s => s.id === b)
                    );
                  }
                  return prev;
                });
              }
            }
          } else if (response.data.hasData) {
            setSectionData(prev => ({
              ...prev,
              [sectionId]: response.data.data
            }));

            if (sectionId !== 'earningsTranscript') {
              setAvailableSections(prev => {
                if (!prev.includes(sectionId)) {
                  return [...prev, sectionId].sort((a, b) =>
                    sectionConfig.findIndex(s => s.id === a) -
                    sectionConfig.findIndex(s => s.id === b)
                  );
                }
                return prev;
              });
            }
          }
        }
      } catch (error) {
        console.error(`Error fetching ${sectionId} data:`, error);
      } finally {
        setSectionLoading(prev => ({ ...prev, [sectionId]: false }));
      }
    };

    // Initial fetch of all data
    const fetchAllData = async () => {
      try {
        let assetType = 'stock';
        let response = await axios.get(`/api/fullquote/${symbol}`);

        if (!response.data.data[0]) {
          response = await axios.get(`/api/indexfullquote/${symbol}`);
          if (!response.data.data[0]) {
            response = await axios.get(`/api/cryptofullquote/${symbol}`);
            assetType = 'crypto';
            activeEndpointRef.current = `/api/cryptofullquote/${symbol}`;
          } else {
            assetType = 'index';
            activeEndpointRef.current = `/api/indexfullquote/${symbol}`;
          }
        } else {
          assetType = 'stock';
          activeEndpointRef.current = `/api/fullquote/${symbol}`;
        }

        setStockData(response.data.data[0]);
        setLoading(false);

        // Save assetType to state
        setAssetType(assetType);  // <-- new state

        // Fetch all section data at once
        await Promise.all(
          sectionConfig.map(section => fetchSectionData(section.id))
        );

        setSectionsLoadComplete(true);
      } catch (error) {
        console.error('Error fetching stock data:', error);
        setLoading(false);
      }
    };


    // Real-time stock price updates
    const fetchStockPrice = async () => {
      if (!activeEndpointRef.current) return;
      try {
        const response = await axios.get(activeEndpointRef.current);
        const newData = response.data.data[0];

        if (previousPrice.current !== null && newData.price !== previousPrice.current) {
          setPriceFlash(true);
          setPriceIncreased(newData.price > previousPrice.current);
          setTimeout(() => setPriceFlash(false), 1000);
        }
        previousPrice.current = newData.price;
        setStockData(newData);
      } catch (error) {
        console.error('Error fetching stock price:', error);
      }
    };

    // Start initial fetch and set up interval
    fetchAllData();
    const intervalId = setInterval(fetchStockPrice, 60000);

    // Cleanup function
    return () => {
      clearInterval(intervalId);
      setStockData(null);
      setSectionData({});
      setAvailableSections([]);
      setSelectedTab(0);
      previousPrice.current = null;
      activeEndpointRef.current = null;
    };
  }, [symbol]);

  // Sync selectedTab with URL after availableSections loads
  useEffect(() => {
    // Don't do anything while initial data is still loading
    if (loading) return;

    // Wait until we have at least some sections loaded
    if (availableSections.length === 0) {
      // Only set to 0 if there's NO URL tab (default overview)
      if (!urlTab) {
        setSelectedTab(0);
      }
      // Otherwise keep null and wait for data
      return;
    }

    if (urlTab) {
      // URL has a tab parameter - find it
      const sectionId = urlToSectionId[urlTab];
      const tabIndex = availableSections.indexOf(sectionId);

      if (tabIndex >= 0) {
        setSelectedTab(tabIndex);
        // Clean up URL: Remove subtab for tabs that don't support subtabs
        if (urlSubtab && sectionId !== 'financials' && sectionId !== 'earnings') {
          navigate(`/asset/${symbol}/${urlTab}`, { replace: true });
        }
      } else {
        // Tab not found in availableSections
        // Check if we're still loading or if data simply doesn't exist
        if (sectionsLoadComplete) {
          // All sections loaded, this tab just doesn't have data
          // Redirect to overview
          navigate(`/asset/${symbol}`, { replace: true });
          setSelectedTab(0);
        }
        // else: Still loading, keep waiting
      }
    } else {
      // No tab in URL - default to first tab (overview)
      setSelectedTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSections, urlTab, symbol, navigate, loading, sectionsLoadComplete]);

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);

    // Get the section ID for this tab index
    const sectionId = availableSections[newValue];
    const urlString = sectionIdToUrl[sectionId];

    // Update URL
    navigate(`/asset/${symbol}/${urlString}`, { replace: true });
  };

  const handleStarClick = () => {
    setShowAddToWatchlistModal(true);
  };

  const handleWatchlistSuccess = (message) => {
    // console.log(message);
  };

  const handleCloseModal = () => {
    setShowAddToWatchlistModal(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="calc(100vh - 100px)"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!stockData) {
    return <div>No asset data available</div>;
  }

  // const currencySymbol = currencySymbols[stockData.currency] || '';

  // Filter sections based on available data
  // const visibleSections = sectionConfig.filter(section =>
  //   availableSections.includes(section.id)
  // );

  return (
    <Container maxWidth="xl">
      <Box sx={{ padding: { xs: 0, sm: 2 } }}>
        <Grid container spacing={2}>
          {/* Stock Header Section */}
          <Grid item xs={12}>
            <Paper variant="stats" elevation={3} sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  position: 'relative',
                  width: '100%',
                }}
              >
                <CompanyLogo
                  symbol={symbol}
                  sx={{
                    width: { xs: 40, sm: 80 }, // overrides sizes
                    height: "auto",
                    margin: { xs: "0 auto", sm: 0 },
                  }}
                />

                {/* Info */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4" sx={{ m: 0 }}>
                      {stockData.symbol}
                    </Typography>
                    {!loading && stockData?.exchange && (
                      <MarketStatusIndicator
                        exchange={stockData.exchange}
                        onHolidayInfoUpdate={(info) => setHolidayInfo(info)}
                      />
                    )}
                  </Box>

                  <Typography variant="h5" sx={{ m: 0 }}>
                    {stockData.name}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StockPriceContainer stockData={stockData} todayHoliday={holidayInfo} />
                  </Box>
                </Box>

                {/* Star */}
                {user && (
                  <StarIcon
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      fontSize: 40,
                      cursor: 'pointer',
                      '&:hover': { color: theme.palette.primary.main },
                    }}
                    onClick={handleStarClick}
                  />
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Chart and Stats Section */}
          <Grid container item xs={12} spacing={2}>
            {/* Chart Section */}
            <Grid
              item
              xs={12}
              sm={12}
              lg={8}
              sx={{
                display: 'block',
                mb: { xs: 2, sm: 2, lg: 0 }
              }}
            >
              <Paper
                elevation={3}
                variant='stats'
                sx={{
                  height: '100%',  // Will match the height of stats section
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <InteractiveStockChart symbol={symbol} />
              </Paper>
            </Grid>

            {/* Stats Section */}
            <Grid
              item
              xs={12}
              sm={12}
              lg={4}
            >
              <Paper
                elevation={3}
                variant='stats'
                sx={{
                  padding: 4,
                  height: '100%'
                }}
              >
                <Grid
                  container
                  direction="column"
                  spacing={2}
                >
                  <Grid item xs={12}>
                    <StockStatsPanel stockData={stockData} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* Return Comparison Section */}
          <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, width: '100%' }}>
            <ReturnComparison symbol={symbol} />
          </Box>

          {availableSections.length > 0 && selectedTab !== null && (
            <Grid item xs={12}>
              <AppBar position="static">
                <Tabs
                  value={selectedTab}
                  onChange={handleChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    "& .MuiTab-root": {
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.text.secondary  // For dark mode
                        : theme.palette.secondary.dark,  // For light mode using our new color
                    },
                    "& .Mui-selected": {
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main,
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main,
                    },
                  }}
                >
                  {availableSections.map((sectionId, index) => {
                    const section = sectionConfig.find((s) => s.id === sectionId);
                    return (
                      <Tab
                        key={section.id}
                        label={section.label}
                        sx={{
                          "&.Mui-selected": {
                            color: theme.palette.mode === 'dark'
                              ? theme.palette.primary.main
                              : theme.palette.secondary.main,
                          },
                        }}
                      />
                    );
                  })}
                </Tabs>
              </AppBar>
              <Paper elevation={3} sx={{ padding: 2 }}>
                {selectedTab === null || !availableSections[selectedTab] || sectionLoading[availableSections[selectedTab]] ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  (() => {
                    const currentSectionId = availableSections[selectedTab];
                    const section = sectionConfig.find(s => s.id === currentSectionId);
                    const Component = section?.component;

                    if (!Component) {
                      return (
                        <Box display="flex" justifyContent="center" p={4}>
                          <Typography>Section not available</Typography>
                        </Box>
                      );
                    }

                    // Pass subtab prop only to CombinedFinancials and Earnings
                    const props = { data: sectionData[currentSectionId] };
                    if (currentSectionId === 'financials' || currentSectionId === 'earnings') {
                      props.subtab = urlSubtab;
                    }
                    if (currentSectionId === 'earnings') {
                      props.transcriptData = sectionData['earningsTranscript'] ?? null;
                    }

                    return React.createElement(Component, props);
                  })()
                )}
              </Paper>
            </Grid>
          )}

        </Grid>
      </Box>

      {/* Watchlist Modal */}
      {stockData && (
        <AddToWatchlistModal
          open={showAddToWatchlistModal}
          onClose={handleCloseModal}
          asset={stockData}
          userId={user?._id}
          onSuccess={handleWatchlistSuccess}
          assetType={assetType}
        />
      )}
    </Container>
  );
};

export default Stock;